<template>
  <div class="training-page">
    <section class="tr-hero">
      <div class="tr-hero__glow" />
      <div class="d-flex align-items-start justify-content-between flex-wrap gap-12">
        <div class="d-flex align-items-center">
          <div class="tr-hero__icon mr-1">
            <feather-icon
              icon="BookOpenIcon"
              size="24"
            />
          </div>
          <div>
            <h2 class="mb-0">
              Training Programs
            </h2>
            <p class="mb-0 tr-hero__subtitle">
              Manage training courses, sessions, and attendee progress.
            </p>
          </div>
        </div>
        <div class="tr-hero__actions">
          <button
            type="button"
            class="tr-btn tr-btn--refresh"
            :disabled="dataLoad"
            @click="getAllData(currentPage)"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              :class="{ 'tr-spin-icon': dataLoad }"
            />
            Refresh
          </button>
          <button
            type="button"
            class="tr-btn tr-btn--add"
            @click="$router.push({ name: addComponentName })"
          >
            <feather-icon
              icon="PlusIcon"
              size="16"
            />
            {{ addType }}
          </button>
        </div>
      </div>

      <div class="tr-stats">
        <div class="tr-stat">
          <div class="tr-stat__icon tr-stat__icon--primary">
            <feather-icon
              icon="LayersIcon"
              size="18"
            />
          </div>
          <div>
            <div class="tr-stat__value">
              {{ totalRows }}
            </div>
            <div class="tr-stat__label">
              Total programs
            </div>
          </div>
        </div>
        <div class="tr-stat">
          <div class="tr-stat__icon tr-stat__icon--info">
            <feather-icon
              icon="CalendarIcon"
              size="18"
            />
          </div>
          <div>
            <div class="tr-stat__value">
              {{ pageSessionsCount }}
            </div>
            <div class="tr-stat__label">
              Sessions on page
            </div>
          </div>
        </div>
        <div class="tr-stat">
          <div class="tr-stat__icon tr-stat__icon--success">
            <feather-icon
              icon="UsersIcon"
              size="18"
            />
          </div>
          <div>
            <div class="tr-stat__value">
              {{ pageAttendeesCount }}
            </div>
            <div class="tr-stat__label">
              Attendees on page
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="tr-toolbar">
      <div class="tr-search">
        <feather-icon
          icon="SearchIcon"
          size="15"
          class="tr-search__icon"
        />
        <input
          v-model="searchQuery"
          type="text"
          class="tr-search__input"
          :class="{ 'tr-search__input--has-clear': searchQuery }"
          placeholder="Search training by name… (press Enter)"
          @keyup.enter="onSearch"
        >
        <button
          v-if="searchQuery"
          type="button"
          class="tr-search__clear"
          title="Clear search"
          @click="clearSearch"
        >
          <feather-icon
            icon="XIcon"
            size="14"
          />
        </button>
      </div>
    </div>

    <div
      v-if="dataLoad"
      class="tr-loading"
    >
      <div class="tr-loading__spinner" />
      <p class="mb-0 text-muted">
        Loading training programs…
      </p>
    </div>

    <div
      v-else-if="!trainings.length"
      class="tr-empty"
    >
      <div class="tr-empty__icon">
        <feather-icon
          icon="InboxIcon"
          size="30"
        />
      </div>
      <h5 class="mb-50">
        No training programs found
      </h5>
      <p class="mb-0 text-muted">
        {{ searchQuery ? 'Try adjusting your search.' : 'Create a new training program to get started.' }}
      </p>
    </div>

    <div
      v-else
      class="tr-grid"
    >
      <article
        v-for="training in trainings"
        :key="training.id"
        class="tr-card"
      >
        <div class="tr-card__accent" />
        <div class="tr-card__header">
          <div class="tr-card__icon">
            <feather-icon
              icon="AwardIcon"
              size="20"
            />
          </div>
          <h3
            class="tr-card__title"
            :title="training.name"
          >
            {{ training.name }}
          </h3>
        </div>

        <div class="tr-card__metrics">
          <div class="tr-metric tr-metric--sessions">
            <feather-icon
              icon="VideoIcon"
              size="15"
            />
            <div>
              <span class="tr-metric__value">{{ training.training_sessions_count || 0 }}</span>
              <span class="tr-metric__label">Sessions</span>
            </div>
          </div>
          <div class="tr-metric tr-metric--attendees">
            <feather-icon
              icon="UserCheckIcon"
              size="15"
            />
            <div>
              <span class="tr-metric__value">{{ training.attendees_count || 0 }}</span>
              <span class="tr-metric__label">Attendees</span>
            </div>
          </div>
        </div>

        <div class="tr-card__actions">
          <button
            type="button"
            class="tr-action tr-action--view"
            title="View sessions"
            @click="goToSessions(training)"
          >
            <feather-icon
              icon="EyeIcon"
              size="15"
            />
            Sessions
          </button>
          <a
            :href="editLink(training)"
            class="tr-action tr-action--edit"
            title="Edit training"
            target="_blank"
            rel="noopener"
          >
            <feather-icon
              icon="EditIcon"
              size="15"
            />
            Edit
          </a>
          <button
            type="button"
            class="tr-action tr-action--delete"
            title="Delete training"
            :disabled="deleteLoadingId === training.id"
            @click="deleteTraining(training.id)"
          >
            <feather-icon
              icon="Trash2Icon"
              size="15"
            />
            Delete
          </button>
        </div>
      </article>
    </div>

    <div
      v-if="!dataLoad && totalRows > perPage"
      class="tr-pagination"
    >
      <div class="tr-pagination__info">
        Showing
        <span class="font-weight-bold">{{ rangeStart }}</span>
        -
        <span class="font-weight-bold">{{ rangeEnd }}</span>
        of
        <span class="font-weight-bold">{{ totalRows }}</span>
      </div>
      <b-pagination
        v-model="currentPage"
        :total-rows="totalRows"
        :per-page="perPage"
        align="center"
        first-number
        last-number
        @change="onPageChange"
      />
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { isString } from 'lodash'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import decryptData from '@/store/auth/decryptData'

export default {
  name: 'Training',
  data() {
    return {
      apiUrl: 'training/list',
      addType: 'Add Training',
      addComponentName: 'add-training',
      editComponent: 'edit-training',
      dataLoad: false,
      deleteLoadingId: null,
      trainings: [],
      perPage: 20,
      currentPage: 1,
      totalRows: 0,
      searchQuery: '',
    }
  },
  computed: {
    pageSessionsCount() {
      return this.trainings.reduce((sum, item) => sum + (item.training_sessions_count || 0), 0)
    },
    pageAttendeesCount() {
      return this.trainings.reduce((sum, item) => sum + (item.attendees_count || 0), 0)
    },
    rangeStart() {
      if (!this.totalRows) return 0
      return (this.currentPage - 1) * this.perPage + 1
    },
    rangeEnd() {
      return Math.min(this.currentPage * this.perPage, this.totalRows)
    },
  },
  mounted() {
    if (this.$route.query.search) {
      this.searchQuery = this.$route.query.search
    }
    this.getAllData(1)
  },
  methods: {
    onSearch() {
      this.getAllData(1)
    },
    clearSearch() {
      this.searchQuery = ''
      this.getAllData(1)
    },
    onPageChange(page) {
      this.getAllData(page)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    },
    editLink(training) {
      return this.$router.resolve({
        name: this.editComponent,
        params: {
          id: training.id,
          name: training.name,
        },
      }).href
    },
    goToSessions(training) {
      this.$router.push({
        name: 'show-sessions-training',
        params: {
          id: training.id,
          name: training.name,
        },
      })
    },
    async getAllData(page = 1) {
      this.currentPage = page
      this.dataLoad = true
      try {
        const params = {
          skip: (page - 1) * this.perPage,
        }
        const search = this.searchQuery.trim()
        if (search) {
          params.search = search
        }

        const response = await axios.get(this.apiUrl, { params })
        if (response.status === 200) {
          const { data } = decryptData(response.data.payload)
          const listKey = Object.keys(data || {}).find(key => key !== 'count')
          const list = listKey ? data[listKey] : []
          this.trainings = Array.isArray(list) ? list : []
          this.totalRows = data?.count ?? this.trainings.length
        }
      } catch (e) {
        this.showError(e, 'Failed to load training programs')
      } finally {
        this.dataLoad = false
      }
    },
    deleteTraining(id) {
      this.$swal({
        title: 'Are you sure?',
        text: "You won't be able to revert this!",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Yes, delete it!',
      }).then(result => {
        if (result.isConfirmed) {
          this.deleteLoadingId = id
          axios.delete(`${this.apiUrl}/${id}`)
            .then(() => {
              this.$swal('Deleted!', 'Deleted Successfully.', 'success')
              this.getAllData(this.currentPage)
            })
            .catch(error => {
              const payload = decryptData(error.response.data.payload).message
              let errorMessage = 'Failed to delete training'
              if (isString(payload)) {
                errorMessage = payload
              } else if (payload && typeof payload === 'object') {
                const firstArrayKey = Object.keys(payload).find(key => Array.isArray(payload[key]))
                errorMessage = firstArrayKey ? payload[firstArrayKey][0] : errorMessage
              }
              this.showError(null, errorMessage)
            })
            .finally(() => {
              this.deleteLoadingId = null
            })
        }
      })
    },
    showError(error, fallback) {
      let message = fallback
      try {
        if (error?.response?.data?.payload) {
          const payload = decryptData(error.response.data.payload)
          const msg = payload?.message || payload
          if (typeof msg === 'string') message = msg
        }
      } catch {
        // keep fallback
      }
      this.$toast({
        component: ToastificationContent,
        props: {
          title: message,
          icon: 'BellIcon',
          variant: 'danger',
        },
      })
    },
  },
}
</script>

<style lang="scss" src="./training.scss"></style>
